/**
 * Photo capture — the moment evidence comes into existence (ADR-0001 §4.6).
 *
 * The rule this module exists to enforce: **the checksum is computed over the exact bytes
 * the camera handed us, before those bytes are stored anywhere, and it never changes
 * afterwards.** A checksum computed later — at upload time, or after a resize — proves
 * only that the file did not rot in transit. A checksum computed at capture proves the
 * bytes an adjuster is looking at are the bytes taken on that roof. That is the claim we
 * actually need to be able to make, and it is only true if we make it here.
 *
 * Capture is split in two on purpose, because photo bytes and photo metadata travel by
 * different roads (protocol.ts, `EntityType`):
 *
 *  - **Metadata** — slot, job, checksum, dimensions, captured-at — rides the ordinary
 *    outbox as a `photo` `create` op.
 *  - **Bytes** — potentially megabytes of them — go to object storage via the presigned
 *    upload path, tracked by a separate queue with its own retry budget.
 *
 * They are deliberately not atomic, and the ordering is deliberate: metadata first. A
 * photo row whose bytes have not landed yet is a visible, recoverable "upload pending".
 * Bytes in a bucket with no row pointing at them are garbage nobody will ever find.
 *
 * Nothing here imports React, Dexie, or `fetch` — same constraint as the rest of the
 * package (ADR-0001 §3.1). Storage arrives through `PhotoBlobStore`.
 */

import type { Uuid } from './ids.js';
import type { JsonObject, SyncOp } from './protocol.js';
import { buildOp } from './outbox.js';

/**
 * Where the bytes live on the device until object storage confirms them.
 *
 * This is a separate port from `OutboxStore` because it has a different lifetime rule.
 * An outbox op is released the moment the server confirms it. Photo bytes are released
 * only when object storage confirms *and* the checksum matches on the other side — until
 * then the phone is the only copy that exists.
 */
export interface PhotoBlobStore {
  /**
   * Persists bytes against a photo id. Must be durable before it resolves: the caller
   * enqueues the metadata op immediately after, and a crash in between must leave the
   * bytes recoverable, not the row orphaned.
   */
  put(photoId: Uuid, bytes: Uint8Array, contentType: string): Promise<void>;
  get(photoId: Uuid): Promise<Uint8Array | undefined>;
  /**
   * Forgets local bytes after object storage has confirmed them.
   *
   * The only deletion in the system, and it is not a deletion of evidence — it is
   * releasing a local cache copy of bytes that are durably stored elsewhere. Callers must
   * not call this on the strength of a 2xx alone; see `uploadState`.
   */
  forget(photoId: Uuid): Promise<void>;
  /** Photo ids whose bytes are still only on this device. Drives the upload queue. */
  listPendingUpload(): Promise<readonly Uuid[]>;
  /** Total bytes held locally. A phone with a full disk cannot capture. */
  totalBytes(): Promise<number>;
}

/** Digest seam. Injected so the core stays testable and free of a crypto import. */
export type Sha256 = (bytes: Uint8Array) => Promise<string>;

/**
 * SHA-256 over Web Crypto, which both Node 22 and every target browser provide.
 *
 * Hex rather than base64 because this string ends up in log lines, support emails, and
 * possibly a legal exhibit. Hex survives being retyped by a human; base64 does not.
 */
export const webCryptoSha256: Sha256 = async (bytes) => {
  // `.slice()` detaches the view from any larger buffer Dexie or the file reader may have
  // handed us. Digesting a subarray of a pooled buffer silently hashes the wrong range.
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes.slice().buffer);
  const out = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < out.length; i += 1) hex += HEX[out[i] as number];
  return hex;
};

const HEX: readonly string[] = Array.from({ length: 256 }, (_, i) =>
  i.toString(16).padStart(2, '0'),
);

/** What the caller knows about a photo the instant the shutter closes. */
export interface CaptureInput {
  readonly jobId: Uuid;
  /** Checklist slot this shot was taken for. See `checklist.ts` — ids are a one-way door. */
  readonly slotId: string;
  readonly bytes: Uint8Array;
  readonly contentType: string;
  /** Pixel dimensions, when the platform reports them. Absent is acceptable, wrong is not. */
  readonly width?: number;
  readonly height?: number;
  /**
   * Device location at capture, if permission was granted.
   *
   * Optional forever. A rep who denies location still gets to work — a checklist that
   * refuses to record a shot without GPS just means the shot does not get taken.
   */
  readonly location?: { readonly lat: number; readonly lon: number; readonly accuracyM?: number };
}

/** Identity and clock for the device doing the capturing. */
export interface CaptureContext {
  readonly orgId: Uuid;
  readonly actorId: Uuid;
  readonly deviceId: Uuid;
  readonly nextId: () => Uuid;
  /** Epoch ms. The device's belief about the time — displayed, never used for ordering. */
  readonly now: () => number;
  readonly sha256?: Sha256;
}

export interface CapturedPhoto {
  readonly photoId: Uuid;
  /** Lowercase hex SHA-256 of the exact captured bytes. */
  readonly sha256: string;
  readonly byteLength: number;
  readonly contentType: string;
  /** The metadata op, already stamped and immutable. Hand this to the outbox. */
  readonly op: SyncOp;
}

/** Bytes larger than this are refused rather than silently wedging the upload queue. */
export const MAX_PHOTO_BYTES = 32 * 1024 * 1024;

/**
 * Capture one photo: hash the bytes, persist them, mint the metadata op.
 *
 * Order is load-bearing and is the whole reason this is a function rather than three
 * calls at the call site:
 *
 *  1. Hash first, over the original bytes.
 *  2. Persist bytes durably.
 *  3. Only then mint the op that claims those bytes exist.
 *
 * Run backwards, a crash between steps produces an op referencing bytes that were never
 * written — a photo the office can see, ask about, and never retrieve. Run in this order,
 * the worst case is orphaned bytes with no op, which `listPendingUpload` surfaces and
 * which cost nothing but disk.
 *
 * The caller enqueues `result.op` into the outbox. That is not done here because the
 * enqueue must be atomic with the caller's own local write of the photo row, and only the
 * caller owns that transaction.
 */
export async function capturePhoto(
  input: CaptureInput,
  context: CaptureContext,
  blobs: PhotoBlobStore,
): Promise<CapturedPhoto> {
  if (input.bytes.byteLength === 0) {
    throw new Error('capturePhoto: refusing to capture zero bytes');
  }
  if (input.bytes.byteLength > MAX_PHOTO_BYTES) {
    throw new Error(
      `capturePhoto: ${input.bytes.byteLength} bytes exceeds the ${MAX_PHOTO_BYTES}-byte limit`,
    );
  }

  const digest = context.sha256 ?? webCryptoSha256;
  const sha256 = await digest(input.bytes);

  const photoId = context.nextId();
  await blobs.put(photoId, input.bytes, input.contentType);

  const payload: JsonObject = {
    jobId: input.jobId,
    slotId: input.slotId,
    sha256,
    byteLength: input.bytes.byteLength,
    contentType: input.contentType,
    // `pending` until object storage confirms the bytes against this same checksum.
    uploadState: 'pending',
  };
  if (input.width !== undefined) payload.width = input.width;
  if (input.height !== undefined) payload.height = input.height;
  if (input.location !== undefined) {
    payload.location = {
      lat: input.location.lat,
      lon: input.location.lon,
      ...(input.location.accuracyM !== undefined ? { accuracyM: input.location.accuracyM } : {}),
    };
  }

  const op = buildOp({
    opId: context.nextId(),
    orgId: context.orgId,
    entityType: 'photo',
    entityId: photoId,
    opType: 'create',
    payload,
    clientCreatedAt: new Date(context.now()).toISOString(),
    deviceId: context.deviceId,
    actorId: context.actorId,
  });

  return {
    photoId,
    sha256,
    byteLength: input.bytes.byteLength,
    contentType: input.contentType,
    op,
  };
}

/**
 * Re-hash local bytes and compare against what was recorded at capture.
 *
 * Used before an upload and again before local bytes are released. Storage does go bad —
 * IndexedDB has been known to truncate under quota pressure — and the failure is silent.
 * A mismatch here means the local copy is already corrupt, so the correct response is to
 * keep it and shout, never to overwrite the good remote copy with it.
 */
export async function verifyLocalBytes(
  photoId: Uuid,
  expectedSha256: string,
  blobs: PhotoBlobStore,
  sha256: Sha256 = webCryptoSha256,
): Promise<{ readonly ok: boolean; readonly reason?: string; readonly actual?: string }> {
  const bytes = await blobs.get(photoId);
  if (bytes === undefined) return { ok: false, reason: 'missing' };

  const actual = await sha256(bytes);
  if (actual !== expectedSha256) return { ok: false, reason: 'checksum_mismatch', actual };

  return { ok: true, actual };
}
